import React, { useEffect, useState } from 'react';
import LaboratoryResultItemsList, {
	ILaboratoryResultListItem,
} from '../LaboratoryResultsList/LaboratoryResultItemsList';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
// @ts-ignore
import styles from './LaboratoryResultDescribe.module.scss';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import LinkButtonWithIcon from '../../../../Components/Button/LinkButtonWithIcon';
import { Position } from '../../../Base/Enums/Position.enum';
import {
	LaboratoryResultMise,
	PhraseType,
	useReviewLaboratoryResultMiseMutation,
	useSendLaboratoryResultMiseMutation,
} from '../../../../graphql/graphql';
import useApiClient from '../../../Base/Hooks/useApiClient';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import OverlayLoading from '../../../../Components/OverlayLoading';
import useLaboratoryResultMiseHelper from '../../Hooks/useLaboratoryResultMiseHelper';
import LaboratoryResultsToDescribeTimeout from '../LaboratoryResultsToDescribeList/LaboratoryResultsToDescribeTimeout';
import PredefinedPhraseSelectionModal from '../../../PredefinedPhrases/Components/PredefinedPhraseSelection/PredefinedPhraseSelection/PredefinedPhraseSelectionModal';
import { PredefinedPhrase } from '../../../PredefinedPhrases/Components/PredefinedPhrasesSetup/PredefinedPhrasesList';
import ElectronicSignatureFields from '../../../Base/Components/ElectronicSignatureFields/ElectronicSignatureFields';
import useElectronicSignature from '../../../Certificates/Hooks/useElectronicSignature';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import useLoading from '../../../Base/Hooks/useLoading';
import SavingSpinner from '../../../../Components/Base/Loading/SavingSpinner';

interface Props {
	laboratoryResultMise: LaboratoryResultMise;
	onSuccess: () => void;
}

interface LaboratoryResultDescribeFormValues {
	note: string;
	electronicSignature: boolean;
	electronicSignatureTimestamp: boolean;
}

const validationSchema = yup.object({
	note: yup.string().required('validation.laboratoryResultDescribe.note'),
});

const LaboratoryResultDescribe: React.FC<Props> = ({
	laboratoryResultMise,
	onSuccess,
}) => {
	const { isLoading: isLoadingAiGen, setIsLoading: setIsLoadingAiGen } =
		useLoading();
	const [isLoading, setIsLoading] = useState(false);
	const [isPredefinedPhraseSelectionOpen, setIsPredefinedPhraseSelectionOpen] =
		useState(false);
	const { displayGQLError, displayError } = useApiClient();
	const [sendLaboratoryResultDescription] =
		useSendLaboratoryResultMiseMutation();
	const [generateReview] = useReviewLaboratoryResultMiseMutation();
	const { patient } = useLaboratoryResultMiseHelper(laboratoryResultMise);
	const { formFieldDefaultValues } = useElectronicSignature();

	const handleSubmit = async (values: LaboratoryResultDescribeFormValues) => {
		setIsLoading(true);
		try {
			const response = await sendLaboratoryResultDescription({
				input: {
					laboratoryResultMiseId: laboratoryResultMise.id,
					note: values.note,
					electronicSignature: {
						electronicSignature: values.electronicSignature,
						electronicSignatureTimestamp: values.electronicSignatureTimestamp,
					},
				},
			});
			displayGQLError(response);
			if ('data' in response) {
				onSuccess();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		formMethods.reset({ note: '', ...formFieldDefaultValues });
	}, [formFieldDefaultValues]);

	const noteFieldValue = formMethods.watch('note');

	const handleLoadPredefinedPhrase = (item: PredefinedPhrase) => {
		setIsPredefinedPhraseSelectionOpen(false);
		const newValue =
			noteFieldValue !== '' ? `${noteFieldValue}\n\n${item.value}` : item.value;
		formMethods.setValue('note', newValue);
	};

	const handleGenerate = async () => {
		setIsLoadingAiGen(true);
		const response = await generateReview({
			where: { id: laboratoryResultMise.id },
		}).unwrap();
		if (response?.reviewLaboratoryResultMise?.response) {
			formMethods.setValue(
				'note',
				response.reviewLaboratoryResultMise.response,
			);
		}
		setIsLoadingAiGen(false);
	};

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div className={styles.wrapper}>
				<div className={styles.header}>
					<div className={styles.title}>{patient}</div>
					<LaboratoryResultsToDescribeTimeout
						laboratoryResultMise={laboratoryResultMise}
					/>
				</div>
				<div className={styles.content}>
					<div className={styles.table}>
						<ScrollContainer>
							<LaboratoryResultItemsList
								items={
									laboratoryResultMise.laboratoryResult
										?.laboratoryResultItems as ILaboratoryResultListItem[]
								}
							/>
						</ScrollContainer>
					</div>
					<div className={styles.sidePanel}>
						<FormWrapper formMethods={formMethods}>
							<TextAreaField
								name='note'
								label='base.specify'
								isRequired
								isWrapped
							/>
							<div className={styles.genWrapper}>
								{isLoadingAiGen ? (
									<SavingSpinner tx='base.evaluationInProgress' />
								) : (
									<LinkButton
										title='base.evaluateByAI'
										theme={Theme.BLUE}
										onClick={handleGenerate}
									/>
								)}
							</div>
							<ElectronicSignatureFields />
							<div className={styles.controls}>
								<SubmitButton
									onSubmit={values =>
										handleSubmit(
											values as unknown as LaboratoryResultDescribeFormValues,
										)
									}
									title='base.shared.save'
								/>
								<LinkButtonWithIcon
									title='base.predefinedPhrases'
									iconPosition={Position.LEFT}
									icon='/dist/icons/react/chat-lines-blue.svg'
									onClick={() => setIsPredefinedPhraseSelectionOpen(true)}
								/>
							</div>
						</FormWrapper>
					</div>
				</div>
			</div>
			{isPredefinedPhraseSelectionOpen && (
				<PredefinedPhraseSelectionModal
					isOpen
					type={PhraseType.LaboratoryResult}
					onSelect={handleLoadPredefinedPhrase}
					onClose={() => setIsPredefinedPhraseSelectionOpen(false)}
				/>
			)}
		</>
	);
};

export default LaboratoryResultDescribe;
